<template>
  <div ref="chartRoot" class="hedging-container">
    <div class="hedging-card" :class="{ 'animate-in': animated }">

      <!-- Header -->
      <div class="hedging-header">
        <div class="hedging-header-label">Hedging Positions</div>
        <div class="hedging-header-row">
          <span class="hedging-mtm-label">MTM</span>
          <span class="hedging-mtm-value">€7.7m</span>
          <span class="hedging-mtm-delta negative">↘ €-41.3k</span>
        </div>
      </div>

      <!-- 2×2 grid of sparklines -->
      <div class="hedging-grid">
        <div
          v-for="(item, i) in metrics"
          :key="item.label"
          class="hedging-metric-card"
          :class="{ 'animate-card': animated }"
          :style="animated ? { animationDelay: (i * 100) + 'ms' } : {}"
        >
          <div class="metric-top-row">
            <span class="metric-label">{{ item.label }}</span>
            <span class="metric-value" :class="item.positive ? 'positive' : 'negative'">{{ item.value }}</span>
          </div>
          <div class="metric-sub-row">
            <span class="metric-sub">{{ item.sub }}</span>
            <span class="metric-delta" :class="item.positive ? 'positive' : 'negative'">{{ item.delta }}</span>
          </div>
          <vue-apex-charts
            :key="chartKey + '-' + i"
            type="area"
            height="55"
            :options="item.chartOptions"
            :series="item.series"
          />
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'

const redOptions = {
  chart: {
    type: 'area',
    sparkline: { enabled: true },
    toolbar: { show: false },
    animations: {
      enabled: true,
      speed: 800,
      easing: 'easeinout'
    }
  },
  stroke: { curve: 'smooth', width: 1.5, colors: ['#EF4444'] },
  fill: {
    type: 'solid',
    colors: ['#FEE2E2'],
    opacity: 0.6
  },
  tooltip: { enabled: false },
  xaxis: { labels: { show: false }, axisBorder: { show: false }, axisTicks: { show: false } },
  yaxis: { labels: { show: false } },
  grid: { show: false },
  colors: ['#EF4444']
}

const greenOptions = {
  chart: {
    type: 'area',
    sparkline: { enabled: true },
    toolbar: { show: false },
    animations: {
      enabled: true,
      speed: 800,
      easing: 'easeinout'
    }
  },
  stroke: { curve: 'smooth', width: 1.5, colors: ['#22C55E'] },
  fill: {
    type: 'solid',
    colors: ['#DCFCE7'],
    opacity: 0.6
  },
  tooltip: { enabled: false },
  xaxis: { labels: { show: false }, axisBorder: { show: false }, axisTicks: { show: false } },
  yaxis: { labels: { show: false } },
  grid: { show: false },
  colors: ['#22C55E']
}

export default {
  name: 'HedgingPositionsChart',

  components: { VueApexCharts },

  data () {
    return {
      animated: false,
      chartKey: 0,

      metrics: [
        {
          label: 'EUR-GBP',
          sub: 'Forward • Sep 2022',
          value: '€-7.69m',
          delta: '€-26.4k',
          positive: false,
          chartOptions: redOptions,
          series: [{
            name: 'MTM',
            data: [12, 10, 13, 11, 9, 12, 8, 11, 7, 10, 6, 8, 5, 9, 4, 6, 3, 5, 2]
          }]
        },
        {
          label: 'EUR-JPY',
          sub: 'Forward • Apr 2023',
          value: '€1.31m',
          delta: '€+2.65k',
          positive: true,
          chartOptions: greenOptions,
          series: [{
            name: 'MTM',
            data: [3, 5, 2, 6, 4, 8, 5, 7, 4, 9, 6, 11, 8, 10, 9, 13, 11, 15]
          }]
        },
        {
          label: 'EUR-GBP',
          sub: 'Forward • Jan 2023',
          value: '€-827k',
          delta: '€-10.5k',
          positive: false,
          chartOptions: redOptions,
          series: [{
            name: 'MTM',
            data: [11, 9, 13, 10, 8, 12, 7, 10, 5, 9, 4, 7, 6, 4, 8, 3, 5, 2]
          }]
        },
        {
          label: 'EUR-Brent Oil',
          sub: 'Option • Apr 2023',
          value: '€-494k',
          delta: '€-7.08k',
          positive: false,
          chartOptions: redOptions,
          series: [{
            name: 'MTM',
            data: [10, 12, 8, 11, 9, 6, 10, 7, 5, 8, 4, 7, 3, 6, 5, 3, 4, 2]
          }]
        }
      ]
    }
  },

  mounted () {
    this._observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.animated = false
            this.$nextTick(() => {
              this.chartKey += 1
              this.animated = true
            })
          } else {
            this.animated = false
          }
        })
      },
      { threshold: 0.3 }
    )
    this._observer.observe(this.$refs.chartRoot)
  },

  beforeDestroy () {
    if (this._observer) this._observer.disconnect()
  }
}
</script>

<style scoped>
.hedging-container {
  display: flex;
  justify-content: center;
  padding: 8px 0;
}

.hedging-card {
  background: #ffffff;
  border-radius: 16px;
  padding: 16px;
  width: 100%;
  max-width: 450px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
  opacity: 0;
  transform: translateY(16px);
  transition: opacity 0.5s ease, transform 0.5s ease;
}

.hedging-card.animate-in {
  opacity: 1;
  transform: translateY(0);
}

/* Header */
.hedging-header {
  margin-bottom: 12px;
}

.hedging-header-label {
  font-size: 11px;
  color: #94A3B8;
  font-weight: 500;
  margin-bottom: 4px;
}

.hedging-header-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.hedging-mtm-label {
  font-size: 18px;
  color: #0f172a;
  font-weight: 500;
}

.hedging-mtm-value {
  font-size: 32px;
  font-weight: 600;
  color: #0f172a;
  letter-spacing: -0.5px;
  line-height: 1;
}

.hedging-mtm-delta {
  font-size: 12px;
  font-weight: 600;
}

/* 2×2 grid */
.hedging-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.hedging-metric-card {
  background: #F8FAFC;
  border-radius: 10px;
  padding: 10px 10px 0;
  overflow: hidden;
  opacity: 0;
}

.hedging-metric-card.animate-card {
  animation: metricFadeIn 0.45s ease forwards;
}

@keyframes metricFadeIn {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}

.metric-top-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 2px;
}

.metric-label {
  font-size: 11px;
  font-weight: 700;
  color: #1e293b;
}

.metric-value {
  font-size: 12px;
  font-weight: 700;
}

.metric-sub-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.metric-sub {
  font-size: 9px;
  color: #94A3B8;
}

.metric-delta {
  font-size: 10px;
  font-weight: 600;
}

.positive {
  color: #16A34A;
}

.negative {
  color: #DC2626;
}
</style>
